import { AxiosResponse } from 'axios';
import { call, delay, put, takeLatest } from 'redux-saga/effects';
import { ISagaFunc } from 'services/actionConfigs';
import { PaginatedList } from 'services/commonTypes';
import { setPageLoading } from 'widgets/PageLoading';
import apiReport from './apis';
import { actions } from './slice';
import {
  IDateRangeParams,
  IInventoryProduct,
  IInventorySearchParams,
  IInventorySummary,
  IOutOfStockSearchParams,
  IPaymentSummary,
  IPaymentTypeItem,
  IPaymentTypeParams,
  IProductDetailParams,
  IProductDetailSummary,
  IProductSale,
  IProductSaleDetail,
  IProductSaleRankingParams,
  IReportSummary,
  ISummaryProductsParams,
  TimeFilter,
} from './types';

const getSummary: ISagaFunc<IDateRangeParams> = function* ({ payload }) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: IReportSummary }> = yield call(
      apiReport.get.summary,
      payload
    );
    yield put(actions.api.get.summary.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.summary.fail(
        error.response?.data?.message || 'Failed to fetch summary'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const getSummaryPaymentType: ISagaFunc<IDateRangeParams> = function* ({
  payload,
}) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: IPaymentSummary }> = yield call(
      apiReport.get.summaryPaymentType,
      payload
    );
    yield put(actions.api.get.summaryPaymentType.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.summaryPaymentType.fail(
        error.response?.data?.message || 'Failed to fetch payment summary'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const getSummaryInventory: ISagaFunc<IDateRangeParams> = function* ({
  payload,
}) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: IInventorySummary }> = yield call(
      apiReport.get.summaryInventory,
      payload
    );
    yield put(actions.api.get.summaryInventory.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.summaryInventory.fail(
        error.response?.data?.message || 'Failed to fetch inventory summary'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const getProductDetailSummary: ISagaFunc<IProductDetailParams> = function* ({
  payload,
}) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: IProductDetailSummary }> = yield call(
      apiReport.get.productDetailSummary,
      payload
    );
    yield put(actions.api.get.summaryProductDetail.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.summaryProductDetail.fail(
        error.response?.data?.message || 'Failed to fetch product detail summary'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const getInventory: ISagaFunc<IInventorySearchParams> = function* ({
  payload,
}) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: PaginatedList<IInventoryProduct> }> =
      yield call(apiReport.get.inventory, payload);
    yield put(actions.api.get.inventory.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.inventory.fail(
        error.response?.data?.message || 'Failed to fetch inventory'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const getOutOfStock: ISagaFunc<IOutOfStockSearchParams> = function* ({
  payload,
}) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: PaginatedList<IInventoryProduct> }> =
      yield call(apiReport.get.outOfStock, payload);
    yield put(actions.api.get.outOfStock.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.outOfStock.fail(
        error.response?.data?.message || 'Failed to fetch out of stock products'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const getSummaryProducts: ISagaFunc<ISummaryProductsParams> = function* ({
  payload,
}) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: PaginatedList<IProductSale> }> =
      yield call(apiReport.get.summaryProducts, payload);
    yield put(actions.api.get.summaryProducts.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.summaryProducts.fail(
        error.response?.data?.message || 'Failed to fetch product summary'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const getProductDetail: ISagaFunc<IProductDetailParams> = function* ({
  payload,
}) {
  yield delay(200);
  setPageLoading(true);
  try {
    const res: AxiosResponse<{ data: PaginatedList<IProductSaleDetail> }> =
      yield call(apiReport.get.productDetail, payload);
    yield put(actions.api.get.productDetail.success(res.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.productDetail.fail(
        error.response?.data?.message || 'Failed to fetch product detail'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const getPaymentType: ISagaFunc<IPaymentTypeParams> = function* ({ payload }) {
  yield delay(200);
  setPageLoading(true);
  try {
    const response: AxiosResponse<{ data: PaginatedList<IPaymentTypeItem> }> =
      yield call(apiReport.get.paymentType, {
        startDate: payload.startDate,
        endDate: payload.endDate,
        paymentType: payload.paymentType,
        page: payload.page,
        size: payload.size,
        keySearch: payload.keySearch,
      });

    yield put(actions.api.get.paymentType.success(response.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.paymentType.fail(
        error.response?.data?.message || 'Failed to fetch payment type data'
      )
    );
  } finally {
    setPageLoading(false);
  }
};

const getProductSale: ISagaFunc<IProductSaleRankingParams> = function* ({ payload }) {
  yield delay(200);
  try {
    const response: AxiosResponse = yield call(apiReport.get.productSale, payload);

    yield put(actions.api.get.productSale.success(response.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.productSale.fail(
        error.response?.data?.message || 'Failed to fetch data'
      )
    );
  }
};

const getSummaryProductSale: ISagaFunc<IDateRangeParams> = function* ({ payload }) {
  yield delay(200);
  try {
    const response: AxiosResponse = yield call(apiReport.get.summaryProductSale, payload);

    yield put(actions.api.get.summaryProductSale.success(response.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.summaryProductSale.fail(
        error.response?.data?.message || 'Failed to fetch data'
      )
    );
  }
};

const getProductSaleChart: ISagaFunc<{type: TimeFilter}> = function* ({ payload }) {
  yield delay(200);
  try {
    const response: AxiosResponse = yield call(apiReport.get.productSaleChart, payload);

    yield put(actions.api.get.productSaleChart.success(response.data.data));
  } catch (error: any) {
    yield put(
      actions.api.get.productSaleChart.fail(
        error.response?.data?.message || 'Failed to fetch data'
      )
    );
  }
};

export default function* sagas() {
  yield takeLatest(actions.api.get.summary.fetch.type, getSummary);
  yield takeLatest(
    actions.api.get.summaryPaymentType.fetch.type,
    getSummaryPaymentType
  );
  yield takeLatest(
    actions.api.get.summaryInventory.fetch.type,
    getSummaryInventory
  );
  yield takeLatest(
    actions.api.get.summaryProductDetail.fetch.type,
    getProductDetailSummary
  );
  yield takeLatest(actions.api.get.inventory.fetch.type, getInventory);
  yield takeLatest(actions.api.get.outOfStock.fetch.type, getOutOfStock);
  yield takeLatest(
    actions.api.get.summaryProducts.fetch.type,
    getSummaryProducts
  );
  yield takeLatest(actions.api.get.productDetail.fetch.type, getProductDetail);
  yield takeLatest(actions.api.get.paymentType.fetch.type, getPaymentType);
  yield takeLatest(actions.api.get.summaryProductSale.fetch.type, getSummaryProductSale);
  yield takeLatest(actions.api.get.productSale.fetch.type, getProductSale);
  yield takeLatest(actions.api.get.productSaleChart.fetch.type, getProductSaleChart);
}
